import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Check, Plus, MailCheck, MailWarning, Send, MailPlus, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import EmptyState from "@/components/common/EmptyState";
import PageHeader from "@/components/layout/PageHeader";
import SectionCard from "@/components/common/SectionCard";
import SearchField from "@/components/common/SearchField";
import StatusBadge from "@/components/common/StatusBadge";
import UserAvatar from "@/components/common/UserAvatar";
import TablePageSkeleton from "@/components/organization/skeletons/TablePageSkeleton";
import SendInviteModal from "@/components/organization/SendInviteModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn, formatRelativeTime } from "@/utils/common.utils";
import { DEFAULT_COUNTRY, toE164 } from "@/lib/phone";
import type { CountryCode } from "libphonenumber-js/max";
import {
  useGetInvitations,
  usePostDealInvite,
  usePostOrgInvite,
  usePostResendInvitation,
  usePostRevokeInvitation,
} from "@/hooks/useInvitations";
import { useGetExternalTeam, useGetInternalTeam } from "@/hooks/useTeam";
import { useBillingGate } from "@/hooks/useBillingGate";
import { useGetBillingStatus } from "@/hooks/useBilling";
import { useGetDeals } from "@/hooks/useDeals";
import { INVITATION_STATUS_STYLES, INVITATION_VIEW_TABS } from "@/constant/organization.constant";
import { EmailDeliveryStatus, InvitableInternalRole, InvitationKind, InvitationStatus, ParticipantRole } from "@/enum/common.enum";
import type { ExternalContact, TeamMember } from "@/types/team.types";

const STATUS_STYLES = INVITATION_STATUS_STYLES as Record<string, string>;

export default function InviteOutbox() {
  const [showNew, setShowNew] = useState(false);
  const [tab, setTab] = useState<InvitationKind>(InvitationKind.ORGANIZATION);
  const [viewTab, setViewTab] = useState<InvitationKind>(InvitationKind.ORGANIZATION);
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const [officeFilter, setOfficeFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [dealFilter, setDealFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; email: string } | null>(null);

  const isInternal = viewTab === InvitationKind.ORGANIZATION;
  // Invitations are a write, so the server refuses them while billing has
  // lapsed. Say so on the button rather than after the form is filled in.
  const { blocked: billingBlocked, blockedReason, billingHref } = useBillingGate();
  // Seat cap is a plan limit, not a billing lapse — `useBillingGate` only covers
  // past_due/canceled. The server's 409 on `POST /invitations/org` was previously
  // the only place this surfaced, so the form opened, filled, and submitted before
  // the user learned they were already at capacity (BUG-005).
  const { data: billingStatus } = useGetBillingStatus();
  // Blocked only when *no* seat of any kind is free. Seats are split into agent
  // seats and one assistant/admin seat: an Individual owner fills the agent seat
  // from day one (`at_seat_limit`) while the assistant seat is still open for a
  // coordinator. Which role fits which seat is the server's call — its 409 says
  // which kind of seat is full. Falls back to `at_seat_limit` for an older API.
  const seatUsage = billingStatus?.usage;
  const atSeatLimit = seatUsage
    ? (seatUsage.can_invite_internal === undefined ? Boolean(seatUsage.at_seat_limit) : !seatUsage.can_invite_internal)
    : false;
  const internalInviteBlocked = billingBlocked || atSeatLimit;
  const internalInviteBlockedReason = billingBlocked ? blockedReason : (atSeatLimit ? "Every seat on your plan is in use — add a seat or upgrade to invite more teammates." : undefined);

  // Search and every facet are server-side query params. Only the visible tab
  // fetches — there is no reason to load the external roster to render the
  // internal one.
  const { data: internal, isLoading: internalLoading } = useGetInternalTeam(
    { search, office: officeFilter || undefined, team_id: teamFilter || undefined, limit: 100 },
    { enabled: isInternal },
  );
  const { data: external, isLoading: externalLoading } = useGetExternalTeam(
    {
      search,
      deal_id: dealFilter || undefined,
      participant_role: (roleFilter as ParticipantRole) || undefined,
      limit: 100,
    },
    { enabled: !isInternal },
  );

  // Invitations are still needed for the row actions (token to copy, resend,
  // revoke) — the roster endpoints deliberately don't expose invite tokens.
  const { data: invitesData } = useGetInvitations();
  const invitesById = new Map((invitesData ?? []).map((inv) => [inv.id, inv]));

  const { data: dealsData } = useGetDeals({ limit: 500 });
  const deals = dealsData?.deals ?? [];

  const { mutateAsync: postOrgInvite } = usePostOrgInvite();
  const { mutateAsync: postDealInvite } = usePostDealInvite();
  const { mutate: revokeInvitation } = usePostRevokeInvitation();
  const { mutate: resendInvitation } = usePostResendInvitation();

  const loading = isInternal ? internalLoading : externalLoading;
  const members: TeamMember[] = internal?.members ?? [];
  const contacts: ExternalContact[] = external?.contacts ?? [];
  const isEmpty = isInternal ? members.length === 0 : contacts.length === 0;

  const openNew = (kind: InvitationKind) => {
    setTab(kind);
    setShowNew(true);
  };

  // `origin_url` is no longer accepted — the invite link's host is server-side
  // config, since it decides where an emailed credential link resolves to.
  // Values arrive from a zod-validated form, so the casts below are narrowing
  // something already checked, not asserting over unvalidated input.
  // Awaited, not fire-and-forget: `handleSubmit` in the modal keeps
  // `isSubmitting` true until this resolves, which is what drives the button's
  // pending state. Rejections are swallowed here because the mutation hooks
  // already toast the error — but they must still leave the modal open with the
  // agent's input intact, so the throw is caught rather than left unhandled.
  const send = async (values: Record<string, unknown>) => {
    try {
      if (tab === InvitationKind.ORGANIZATION) {
        await postOrgInvite({ email: String(values.email), role: values.role as InvitableInternalRole });
      } else {
        const sendSms = values.send_sms === true;
        // E.164 is resolved here rather than passed through raw: the form field
        // holds what the agent typed ("0312 3456789"), and the country lives in
        // a sibling field the server has no use for. zod already rejected
        // anything `toE164` could not parse, so this cannot silently drop a
        // number the agent asked to text.
        const phone = values.phone
          ? toE164(String(values.phone), (values.phone_country as CountryCode) || DEFAULT_COUNTRY)
          : null;
        await postDealInvite({
          email: String(values.email),
          deal_id: String(values.deal_id),
          participant_role: values.participant_role as ParticipantRole,
          name: values.name ? String(values.name) : undefined,
          phone: phone ?? undefined,
          // Omitted rather than ["email"] when not texting, so the request body
          // is byte-identical to what it was before SMS existed.
          channels: sendSms ? ["email", "sms"] : undefined,
        });
      }
      setShowNew(false);
    } catch {
      /* toasted by the mutation hook; the modal stays open so nothing is retyped */
    }
  };

  const copy = async (token?: string) => {
    if (!token) return;
    await navigator.clipboard.writeText(`${window.location.origin}/invite/${token}`);
    setCopied(token);
    toast.success("Link copied");
    setTimeout(() => setCopied(null), 1800);
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    revokeInvitation({ invitationId: revokeTarget.id, payload: { reason: "manual" } });
    setRevokeTarget(null);
  };

  /** Resend / copy / revoke, shown only for a row that has a pending invite. */
  const RowActions = ({ invitationId, email, status }: {
    invitationId?: string; email: string; status: string;
  }) => {
    const invite = invitationId ? invitesById.get(invitationId) : undefined;
    if (status !== InvitationStatus.PENDING || !invite) {
      return <span className="text-xs text-dash-faint">—</span>;
    }
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="seamless" type="button" className="rounded-lg p-1.5 text-dash-muted hover:bg-dash-hover hover:text-dash-ink" data-testid={`invite-menu-${invite.id}`}>
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => resendInvitation({ invitationId: invite.id, payload: {} })} data-testid={`invite-resend-${invite.id}`}>
            <Send className="mr-2 size-3.5" /> Resend
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => copy(invite.token)} data-testid={`invite-copy-${invite.id}`}>
            {copied === invite.token ? <Check className="mr-2 size-3.5 text-dash-greenDeep" /> : <Copy className="mr-2 size-3.5" />} Copy link
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setRevokeTarget({ id: invite.id, email })} className="text-dash-rust" data-testid={`invite-revoke-${invite.id}`}>
            Revoke
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  /** The delivery hint under a name, driven by the invitation's last email log. */
  const DeliveryHint = ({ invitationId, status }: { invitationId?: string; status: string }) => {
    const invite = invitationId ? invitesById.get(invitationId) : undefined;
    if (!invite) return null;
    if (invite.last_email_sent_at && invite.last_email_status === EmailDeliveryStatus.SENT) {
      return (
        <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-dash-greenDeep" data-testid={`invite-email-sent-${invite.id}`}>
          <MailCheck className="size-3" /> Sent {formatRelativeTime(invite.last_email_sent_at, "just now")}
        </div>
      );
    }
    if (status === InvitationStatus.PENDING
      && (invite.last_email_status === EmailDeliveryStatus.FAILED || invite.last_email_status === EmailDeliveryStatus.SKIPPED)) {
      return (
        <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-dash-rust" title={invite.last_email_error || "Email delivery pending"} data-testid={`invite-email-pending-${invite.id}`}>
          <MailWarning className="size-3" /> Use copy link
        </div>
      );
    }
    return null;
  };

  return (
    <div className="font-dashboard" data-testid="invites-page">
      <PageHeader
        title="Invite Team members"
        description="Manage your team, clients, and partners in your brokerage."
        actions={
          <>
            <Button type="button" variant="dashboardOutline" size="lg" className="h-11 !rounded-xl" disabled={billingBlocked} title={billingBlocked ? blockedReason : undefined} onClick={() => openNew(InvitationKind.DEAL)} data-testid="new-invite-external-btn">
              <Plus data-icon="inline-start" /> Invite External
            </Button>
            <Button type="button" variant="dashboardPrimary" size="lg" className="h-11 !rounded-xl" disabled={internalInviteBlocked} title={internalInviteBlockedReason} onClick={() => openNew(InvitationKind.ORGANIZATION)} data-testid="new-invite-internal-btn">
              <Plus data-icon="inline-start" /> Invite Internal Team Member
            </Button>
          </>
        }
      />

      {atSeatLimit && !billingBlocked && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dash-amber/30 bg-dash-amber/10 px-4 py-3 text-sm text-dash-ink" data-testid="seat-limit-banner">
          <span>You&apos;re at your plan&apos;s seat limit — invite a new teammate by upgrading first.</span>
          {billingHref && (
            <Link to={billingHref} className="font-semibold underline underline-offset-2 hover:text-primary" data-testid="seat-limit-upgrade-link">
              Upgrade plan
            </Link>
          )}
        </div>
      )}

      <div className="mt-6 flex items-center gap-6 border-b border-dash-border">
        {INVITATION_VIEW_TABS.map((item) => (
          <Button variant="seamless"
            key={item.key}
            type="button"
            onClick={() => { setViewTab(item.key as InvitationKind); setSearch(""); }}
            className={cn(
              "-mb-px border-b-2 px-1 pb-3 text-sm transition-colors",
              viewTab === item.key ? "border-dash-ink font-semibold text-dash-ink" : "border-transparent font-medium text-dash-muted hover:text-dash-ink"
            )}
            data-testid={`invites-view-tab-${item.key}`}
          >
            {item.label}
          </Button>
        ))}
      </div>

      <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
        <SearchField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onClear={() => setSearch("")}
          placeholder={isInternal ? "Search agents…" : "Search external partners…"}
          className="w-full sm:w-72"
          testId="invites-search"
        />
        {isInternal ? (
          <>
            {/* Offices come from the server. There is no offices backend yet, so
                the list is empty and the control stays out of the way. */}
            {!!internal?.filters.offices.length && (
              <Select value={officeFilter || "all"} onValueChange={(v) => setOfficeFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="h-11 w-auto gap-2 rounded-xl px-3" data-testid="invites-office-filter">
                  <SelectValue placeholder="All offices" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All offices</SelectItem>
                  {internal.filters.offices.map((office) => <SelectItem key={office} value={office}>{office}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {!!internal?.filters.teams.length && (
              <Select value={teamFilter || "all"} onValueChange={(v) => setTeamFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="h-11 w-auto gap-2 rounded-xl px-3" data-testid="invites-team-filter">
                  <SelectValue placeholder="All teams" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All teams</SelectItem>
                  {internal.filters.teams.map((team) => <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </>
        ) : (
          <>
            <Select value={dealFilter || "all"} onValueChange={(v) => setDealFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="h-11 w-auto gap-2 rounded-xl px-3" data-testid="invites-deal-filter">
                <SelectValue placeholder="All deals" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All deals</SelectItem>
                {(external?.filters.deals ?? []).map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>{deal.address}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={roleFilter || "all"} onValueChange={(v) => setRoleFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="h-11 w-auto gap-2 rounded-xl px-3" data-testid="invites-role-filter">
                <SelectValue placeholder="All roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {(external?.filters.participant_roles ?? []).map((role) => (
                  <SelectItem key={role} value={role}>{role.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      <SectionCard className="mt-5" contentClassName="p-0" data-testid="invites-table">
        {loading ? (
          <TablePageSkeleton rows={6} columns={isInternal ? 9 : 6} showAvatar className="rounded-none border-0 shadow-none" />
        ) : isEmpty ? (
          <EmptyState compact icon={MailPlus} title={isInternal ? "No teammates yet" : "No external partners yet"} description="Invite a teammate or deal collaborator to start working together." className="border-0" />
        ) : (
          <Table className="whitespace-nowrap">
            <TableHeader>
              <TableRow className="border-dash-border hover:bg-transparent">
                <TableHead className="h-11 px-5 text-[11px] font-medium uppercase tracking-wide text-dash-faint">{isInternal ? "Agent" : "Contact"}</TableHead>
                <TableHead className="text-[11px] font-medium uppercase tracking-wide text-dash-faint">Email</TableHead>
                {isInternal ? (
                  <>
                    <TableHead className="text-[11px] font-medium uppercase tracking-wide text-dash-faint">Role</TableHead>
                    <TableHead className="text-[11px] font-medium uppercase tracking-wide text-dash-faint">Team</TableHead>
                    <TableHead className="text-[11px] font-medium uppercase tracking-wide text-dash-faint">Active deals</TableHead>
                    <TableHead className="text-[11px] font-medium uppercase tracking-wide text-dash-faint">Overdue tasks</TableHead>
                    <TableHead className="text-[11px] font-medium uppercase tracking-wide text-dash-faint">Seat</TableHead>
                    <TableHead className="text-[11px] font-medium uppercase tracking-wide text-dash-faint">Last active</TableHead>
                  </>
                ) : (
                  <>
                    <TableHead className="text-[11px] font-medium uppercase tracking-wide text-dash-faint">Deal</TableHead>
                    <TableHead className="text-[11px] font-medium uppercase tracking-wide text-dash-faint">Deal role</TableHead>
                  </>
                )}
                <TableHead className="text-[11px] font-medium uppercase tracking-wide text-dash-faint">Status</TableHead>
                <TableHead className="pr-5 text-right text-[11px] font-medium uppercase tracking-wide text-dash-faint">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isInternal ? members.map((member) => (
                <TableRow key={member.user_id ?? member.invitation_id} className="border-dash-border hover:bg-dash-hover/50" data-testid={`invite-row-${member.user_id ?? member.invitation_id}`}>
                  <TableCell className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <UserAvatar name={member.name || member.email} src={member.photo_url} className="size-8 shrink-0" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-dash-ink">{member.name || member.email.split("@")[0]}</div>
                        <DeliveryHint invitationId={member.invitation_id} status={member.invitation_status} />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-dash-muted">{member.email}</TableCell>
                  <TableCell className="text-dash-muted">{member.role_label}</TableCell>
                  <TableCell className="text-dash-muted">{member.team_name || "—"}</TableCell>
                  <TableCell className="text-dash-ink">{member.active_deals}</TableCell>
                  <TableCell className={member.overdue_tasks > 0 ? "font-medium text-dash-rust" : "text-dash-faint"}>
                    {member.overdue_tasks > 0 ? member.overdue_tasks : "—"}
                  </TableCell>
                  <TableCell>
                    <StatusBadge tone={member.seat_status === "active" ? "success" : "neutral"} className="text-[10px] uppercase tracking-[0.12em]">
                      {member.seat_status}
                    </StatusBadge>
                  </TableCell>
                  {/* null means they have not signed in since sign-in history
                      was instrumented — not that they are inactive. */}
                  <TableCell className="text-dash-faint">
                    {member.last_active_at ? formatRelativeTime(member.last_active_at) : "—"}
                  </TableCell>
                  <TableCell>
                    <StatusBadge tone={STATUS_STYLES[member.invitation_status]} className="text-[10px] uppercase tracking-[0.15em]" data-testid={`invite-status-${member.user_id ?? member.invitation_id}`}>
                      {member.member_status === "invited" ? member.invitation_status : "active"}
                    </StatusBadge>
                  </TableCell>
                  <TableCell className="pr-5 text-right">
                    <RowActions invitationId={member.invitation_id} email={member.email} status={member.invitation_status} />
                  </TableCell>
                </TableRow>
              )) : contacts.map((contact) => (
                <TableRow key={contact.invitation_id} className="border-dash-border hover:bg-dash-hover/50" data-testid={`invite-row-${contact.invitation_id}`}>
                  <TableCell className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <UserAvatar name={contact.name || contact.email} src={contact.photo_url} className="size-8 shrink-0" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-dash-ink">{contact.name || contact.email.split("@")[0]}</div>
                        <DeliveryHint invitationId={contact.invitation_id} status={contact.status} />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-dash-muted">{contact.email}</TableCell>
                  <TableCell className="max-w-[220px] truncate text-dash-muted">{contact.deal_address}</TableCell>
                  <TableCell className="text-dash-muted">{contact.participant_role_label}</TableCell>
                  <TableCell>
                    <StatusBadge tone={STATUS_STYLES[contact.status]} className="text-[10px] uppercase tracking-[0.15em]" data-testid={`invite-status-${contact.invitation_id}`}>{contact.status}</StatusBadge>
                  </TableCell>
                  <TableCell className="pr-5 text-right">
                    <RowActions invitationId={contact.invitation_id} email={contact.email} status={contact.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      {showNew && (
        <SendInviteModal kind={tab} deals={deals} onClose={() => setShowNew(false)} onSubmit={send} />
      )}

      {revokeTarget && (
        <ConfirmDialog
          open
          onOpenChange={(open: boolean) => { if (!open) setRevokeTarget(null); }}
          title="Revoke invitation?"
          description={`${revokeTarget.email} won't be able to use this invite link anymore.`}
          confirmLabel="Revoke"
          busyLabel="Revoking…"
          onConfirm={confirmRevoke}
          testId="revoke-invite-confirm-dialog"
        />
      )}
    </div>
  );
}
